import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useConfetti } from "@/components/ui/confetti-provider";
import { v4 as uuidv4 } from "uuid";

interface UseLikeStateProps {
  id: string;
  isLiked?: boolean;
  likeCount?: number;
  user: any | null;
}

// Function to get or create a persistent anonymous ID
const getAnonymousId = (): string => {
  try {
    let anonymousId = localStorage.getItem("anonymous_user_id");
    if (!anonymousId) {
      anonymousId = `anon-${uuidv4()}`;
      localStorage.setItem("anonymous_user_id", anonymousId);
    }
    return anonymousId;
  } catch (e) {
    // If localStorage is not available, generate a temporary ID
    return `anon-${uuidv4()}`;
  }
};

export const useLikeState = ({
  id,
  isLiked = false,
  likeCount = 0,
  user,
}: UseLikeStateProps) => {
  const [likeState, setLikeState] = useState(isLiked);
  const [localLikeCount, setLocalLikeCount] = useState(likeCount);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { showConfetti } = useConfetti();

  // Update local state when props change
  useEffect(() => {
    setLocalLikeCount(likeCount);
    setLikeState(isLiked);
  }, [likeCount, isLiked]);

  // Check if the current user has liked this profile
  useEffect(() => {
    const checkLikeStatus = async () => {
      try {
        const userId = user?.id || null;
        const anonymousId = userId ? null : getAnonymousId();

        // Check like status using the database function
        const { data, error } = await supabase.rpc("is_profile_liked", {
          p_profile_id: id,
          p_user_id: userId,
          p_anonymous_id: anonymousId,
        });

        if (error) {
          console.error("Error checking like status:", error);
        } else {
          setLikeState(!!data);

          // Sync localStorage with database state for quick access
          try {
            const likedProfiles = JSON.parse(
              localStorage.getItem("likedProfiles") || "[]"
            );
            if (data && !likedProfiles.includes(id)) {
              likedProfiles.push(id);
              localStorage.setItem(
                "likedProfiles",
                JSON.stringify(likedProfiles)
              );
            } else if (!data && likedProfiles.includes(id)) {
              localStorage.setItem(
                "likedProfiles",
                JSON.stringify(
                  likedProfiles.filter((profileId: string) => profileId !== id)
                )
              );
            }
          } catch (storageError) {
            console.warn("Error updating localStorage:", storageError);
          }
        }

        // Also fetch current like count
        // await fetchLikeCount();
      } catch (error) {
        console.error("Error in checkLikeStatus:", error);
      }
    };

    // if (id) {
    //   checkLikeStatus();
    // }
  }, [id, user]);

  // Function to fetch current like count
  const fetchLikeCount = async () => {
    if (!id) return;

    try {
      const { data, error } = await supabase.rpc("get_profile_like_count", {
        p_profile_id: id,
      });

      if (error) {
        console.warn("Error fetching like count:", error);
      } else {
        setLocalLikeCount(data || 0);
      }
    } catch (err) {
      console.error("Error in fetchLikeCount:", err);
    }
  };

  // Handle like action with debouncing for better performance
  const handleLike = async (e: React.MouseEvent, isMobile: boolean) => {
    e.preventDefault(); // Prevent navigation to profile
    e.stopPropagation();

    if (isLoading) return;

    // If already liked on mobile, do nothing (prevent unlike)
    if (isMobile && likeState) {
      return;
    }

    setIsLoading(true);

    try {
      // On mobile, we can only like, not unlike
      const newLikedState = isMobile ? true : !likeState;
      const updatedLikeDelta = newLikedState ? 1 : -1;

      // Only update if the state is changing
      if ((isMobile && !likeState) || !isMobile) {
        // Update UI immediately for responsiveness
        setLikeState(newLikedState);
        setLocalLikeCount((prev) => prev + updatedLikeDelta);

        // Get button position for localized confetti
        if (newLikedState) {
          // Try to get the target element or its parent for positioning
          const targetEl = e.currentTarget as HTMLElement;
          if (targetEl) {
            const rect = targetEl.getBoundingClientRect();
            showConfetti({
              x: rect.left + rect.width / 2,
              y: rect.top + rect.height / 2,
              width: rect.width * 3,
              height: 10,
            });
          }
        }

        // Update like status in local storage for instant feedback
        const likedProfiles = JSON.parse(
          localStorage.getItem("likedProfiles") || "[]"
        );
        const updatedLikedProfiles = newLikedState
          ? [...likedProfiles, id]
          : likedProfiles.filter((profileId: string) => profileId !== id);
        localStorage.setItem(
          "likedProfiles",
          JSON.stringify(updatedLikedProfiles)
        );

        // Use the toggle_profile_like function
        const userId = user?.id || null;
        const anonymousId = userId ? null : getAnonymousId();

        const { data, error } = await supabase.rpc("toggle_profile_like", {
          p_profile_id: id,
          p_user_id: userId,
          p_anonymous_id: anonymousId,
        });

        if (error) {
          console.error("Error toggling like:", error);
          throw error;
        }

        // Update with accurate database values
        if (data && data.length > 0) {
          setLikeState(data[0].liked);
          setLocalLikeCount(data[0].like_count);

          // If this is a new like, trigger notification via edge function
          if (data[0].liked && userId) {
            try {
              await supabase.functions.invoke("send-like-notification", {
                body: {
                  profileId: id,
                  likerId: userId,
                  isAnonymous: false,
                },
              });
            } catch (notifError) {
              // Non-critical error, continue
            }
          }
        }
      }
    } catch (error) {
      console.error("Error updating like:", error);

      // Store the updated delta based on current liked state
      const revertLikeDelta = likeState ? -1 : 1;

      // Revert UI changes if there was an error
      setLikeState(!likeState);
      setLocalLikeCount((prev) => prev + revertLikeDelta);

      // Revert localStorage changes
      try {
        const likedProfiles = JSON.parse(
          localStorage.getItem("likedProfiles") || "[]"
        );
        const updatedLikedProfiles = likeState
          ? [...likedProfiles, id]
          : likedProfiles.filter((profileId: string) => profileId !== id);
        localStorage.setItem(
          "likedProfiles",
          JSON.stringify(updatedLikedProfiles)
        );
      } catch (storageError) {
        console.warn("Error reverting localStorage:", storageError);
      }

      toast({
        title: "Error",
        description: "Failed to update like status. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // This is the wrapper for handleLike to match the expected interface
  const handleLikeToggle = (e: React.MouseEvent) => {
    handleLike(e, false);
  };

  // Simplified version for mobile experience
  const handleMobileLike = (e: React.MouseEvent) => {
    handleLike(e, true);
  };

  return {
    likeState,
    localLikeCount,
    handleLike,
    handleLikeToggle,
    handleMobileLike,
    isLoading,
  };
};
